import _                                                                 from 'underscore';
import { capitalize, formatOption }                                      from '../util';
import { INPUT_TYPES }                                                   from '../constants/FormEnums';
import { INTAKE_QUESTION_CONTEXTS }                                      from '../constants/ProductEnums';
import { TimeService }                                                   from './TimeService';
import { CorvalCategory, CorvalIntakeQuestion, CorvalSubCategory, Uuid } from '../types';
import { CONDITION_TYPES }                                               from '../constants/SystemEnums';

export interface ItemsByCategoryRecordItem<P = any> {
	category: CorvalCategory,
	categoryId: Uuid,
	subCategories: ExtendedCorvalSubCategory<P>[]
}

export interface ExtendedCorvalSubCategory<P = any> extends CorvalSubCategory {
	items: Array<ItemWithCategorizationDetails & P>
}

export interface ItemWithCategorizationDetails {
	category: Pick<CorvalCategory, 'id' | 'ordinal'>,
	subCategory: Pick<CorvalSubCategory, 'id' | 'ordinal'>
}

export type TCategorizationItem<P = any> = ItemWithCategorizationDetails | P;

export class DecisionEngineService {
	
	static isShortTextQuestion = ( value: any ) => [
		INPUT_TYPES.SHORT_TEXT
	].includes( value );
	
	static isLongTextQuestion = ( value: any ) => [
		INPUT_TYPES.LONG_TEXT
	].includes( value );
	
	static isTextTypeQuestion = ( value: any ) => [
		INPUT_TYPES.SHORT_TEXT,
		INPUT_TYPES.LONG_TEXT
	].includes( value );
	
	static isSelectionTypeQuestion = ( value: any ) => [
		INPUT_TYPES.SELECT,
		INPUT_TYPES.SELECT_MULTI
	].includes( value );
	
	static isBooleanTypeQuestion = ( value: any ) => [
		INPUT_TYPES.CHECKBOX
	].includes( value );
	
	static isMultiSectionTypeQuestion = ( value: any ) => [
		INPUT_TYPES.SELECT_MULTI
	].includes( value );
	
	static isBoundedValueTypeQuestion = ( value: any ) => [
		INPUT_TYPES.SELECT,
		INPUT_TYPES.SELECT_MULTI,
		INPUT_TYPES.CHECKBOX
	].includes( value );
	
	static isDateQuestion = ( value: any ) => [
		INPUT_TYPES.DATE
	].includes( value );
	
	static isDateRangeQuestion = ( value: any ) => [
		INPUT_TYPES.DATE_RANGE
	].includes( value );
	
	static isDateTypeQuestion = ( value: any ) => [
		INPUT_TYPES.DATE,
		INPUT_TYPES.DATE_RANGE
	].includes( value );
	
	static isCoreTypeQuestion = ( value: any ) => [
		INTAKE_QUESTION_CONTEXTS.CORE
	].includes( value );
	
	static isProjectTypeQuestion = ( value: any ) => [
		INTAKE_QUESTION_CONTEXTS.PROJECT
	].includes( value );
	
	static isAddititveTypeQuestion = ( value: any ) => [
		INTAKE_QUESTION_CONTEXTS.ADDITIVE
	].includes( value );
	
	/* Generates dropdown choices for contest based on a collection of intake questions */
	static generateChoiceTypeQuestionOptions = ( questions: CorvalIntakeQuestion[] ) => {
		return _.chain( questions )
		        .filter( q => DecisionEngineService.isBoundedValueTypeQuestion( q.inputType ) )
		        .map( q => formatOption( q.id, q.name ) )
		        .value()
	};
	
	/* Finds a question's attribute within a given collection of intake questions,
	 * Can return a whole question if no attribute specified, or null. */
	static getQuestionInSet = ( questions: CorvalIntakeQuestion[], id: Uuid ) => {
		
		return DecisionEngineService.getAttributeFromQuestionInSet( questions, id, null, true );
	};
	
	/* Finds a question's attribute within a given collection of intake questions,
	 * Can return a whole question if no attribute specified, or null. */
	static getAttributeFromQuestionInSet = ( questions: CorvalIntakeQuestion[], id: Uuid, attribute: keyof CorvalIntakeQuestion, returnQuestionOnUndefined = false ) => {
		
		// works in conjunctino with `getItemsReq` request
		let question = _.findWhere( questions, { id } );
		
		if( question ){
			return ( attribute ) ? question[ attribute ] : returnQuestionOnUndefined ? question : null
		}
		
		return null;
	};
	
	static valueConsideredUnanswered = ( value: unknown, inputType: INPUT_TYPES ) => {
		
		let formattedValue = DecisionEngineService.formatValueForInputType( value, inputType );
		
		if( _.isNull( formattedValue ) ) return true;
		
		switch( inputType ){
			case INPUT_TYPES.CHECKBOX :
				return ( !_.isBoolean( value ) && ![ 'true', 'false' ].includes( formattedValue as string ) );
			case INPUT_TYPES.SELECT_MULTI :
				return !_.isArray( formattedValue ) || _.isEmpty( _.compact( formattedValue ) ); //In JavaScript, false, null, 0, "", undefined and NaN are all falsy
			case INPUT_TYPES.SELECT :
			case INPUT_TYPES.SHORT_TEXT :
			case INPUT_TYPES.LONG_TEXT :
			case INPUT_TYPES.DATE :
			case INPUT_TYPES.DATE_RANGE :
				return !_.isString( formattedValue ) || _.isEmpty( formattedValue );
			default:
				return false;
		}
	}
	
	static formatValueForInputType = ( value: unknown, inputType: INPUT_TYPES ) => {
		
		if( _.isNull( value ) ) return value;
		
		switch( inputType ){
			case INPUT_TYPES.CHECKBOX :
				return _.isBoolean( value ) ? value ? 'true' : 'false' : value;
			case INPUT_TYPES.SELECT_MULTI :
				return _.isArray( value ) ? value.join( TimeService.dateRangeTansmissionDelimiter ) : value;
			case INPUT_TYPES.DATE_RANGE :
				return _.isString( value ) ? value.split( TimeService.dateRangeDisplayDelimiter ).map( TimeService.displayDateToISO ).join( ',' ) : value;
			default:
				return value;
		}
	};
	
	static formatInputValueFromInputType = ( value: unknown, inputType: INPUT_TYPES ) => {
		
		if( _.isNull( value ) ) return value;
		
		switch( inputType ){
			case INPUT_TYPES.CHECKBOX :
				return _.isString( value ) ? ( value.toLowerCase() === 'true' ) : value;
			case INPUT_TYPES.SELECT_MULTI :
				return _.isString( value ) ? value.split( TimeService.dateRangeTansmissionDelimiter ) : value;
			case INPUT_TYPES.DATE_RANGE :
				return _.isString( value ) ? value.split( TimeService.dateRangeTansmissionDelimiter ).map( v => TimeService.ISOtoDisplayDate( v ) ).join( TimeService.dateRangeDisplayDelimiter ) : value;
			default:
				return value;
		}
	};
	
	static getConditionValueFromConditionType = ( value: CONDITION_TYPES ) => {
		
		switch( value ){
			case CONDITION_TYPES.CONTAINS:
				return 'contains';
			case CONDITION_TYPES.NOT_CONTAINS:
				return 'does not contain';
			case CONDITION_TYPES.EQUALTO:
				return 'is';
			case CONDITION_TYPES.NOT_EQUALTO:
				return 'is not';
			case CONDITION_TYPES.LESSTHAN:
				return 'is less than';
			case CONDITION_TYPES.GREATERTHAN:
				return 'is greater than';
		}
		return value;
	}
	
	static createCategoryStructureFromItems = ( items: TCategorizationItem[] | ItemWithCategorizationDetails[] ): ItemsByCategoryRecordItem[] => {
		
		return _.chain( items )
		        .groupBy( item => item.category.id )
		        .mapObject( ( itemInCategory, categoryId ) => {
			
			        let category = _.first( itemInCategory ).category;
			
			        let subCategories = _.chain( itemInCategory )
			                             .pluck( 'subCategory' )
			                             .uniq( subcategory => subcategory.id )
			                             .sortBy( 'ordinal' )
			                             .value();
			
			        return { category, categoryId, subCategories }
		        } )
		        .values()
		        .sortBy( ( item: ItemsByCategoryRecordItem ) => item.category.ordinal )
		        .value();
	}
	
	static createCategoricallyStructuredItemSet = ( items: TCategorizationItem[] | ItemWithCategorizationDetails[], filterMethod: ( item: any, subCategory: ItemWithCategorizationDetails['subCategory'] ) => any = null ): ItemsByCategoryRecordItem[] => {
		
		return DecisionEngineService.createCategoryStructureFromItems( items ).map( set => {
			
			set.subCategories.map( ( subCategory: ExtendedCorvalSubCategory ) => {
				
				subCategory.items = _.chain( items )
				                     .filter( item => ( filterMethod ? filterMethod( item, subCategory ) : ( item.subCategory.id === subCategory.id ) ) )
				                     .sortBy( 'ordinal' )
				                     .value()
				
				return subCategory;
			} )
			
			return set;
		} );
	}
	
	static createGroupedItemsBySubCategorySets = ( items: TCategorizationItem[] | ItemWithCategorizationDetails[] ): Record<string, ItemWithCategorizationDetails[]> => {
		
		return _.chain( items )
		        .groupBy( item => item.subCategory.id )
		        .mapObject( item => _.sortBy( item, 'ordinal' ) )
		        .value();
	}
}
